<div class="flex flex-col h-full max-h-[100dvh] w-full bg-white dark:bg-zinc-900">
  <!-- Header -->
  <div class="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-700 shrink-0">
    <div>
      <h2 data-testid="field-dialog-heading" class="text-xl font-semibold text-zinc-900 dark:text-white">{{ dialogTitle() }}</h2>
      <p class="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">Fields are the building blocks of your data model — group them into structures for templates.</p>
    </div>
    <button
      type="button"
      aria-label="Close"
      class="rounded-full p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors cursor-pointer"
      (click)="onCancel()"
    >
      <mat-icon class="!text-xl text-zinc-500 dark:text-zinc-400">close</mat-icon>
    </button>
  </div>

  <!-- Form -->
  <form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col flex-1 min-h-0">
    <div class="flex-1 overflow-y-auto px-6 py-5 space-y-8">

      <!-- ============ Section: Basics ============ -->
      <section class="space-y-4">
        <h3 class="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 border-b border-zinc-100 dark:border-zinc-800 pb-1.5">Basics</h3>

        <!-- Field Name -->
        <div class="space-y-1.5">
          <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Field Name <span class="text-red-500">*</span></label>
          <input
            data-testid="field-dialog-name-input"
            type="text"
            formControlName="name"
            placeholder="e.g., emailAddress"
            class="flex h-9 w-full rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-zinc-100 px-3 py-1.5 text-sm shadow-sm transition-colors placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
            [class.border-red-300]="form.get('name')?.invalid && form.get('name')?.touched"
            [class.ring-red-500]="form.get('name')?.invalid && form.get('name')?.touched"
          >
          @if (form.get('name')?.hasError('required') && form.get('name')?.touched) {
            <p class="text-xs text-red-500 mt-1">Name is required</p>
          }
        </div>

        <!-- Category -->
        <div class="space-y-1.5">
          <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Category <span class="text-red-500">*</span></label>
          <input
            data-testid="field-dialog-category-input"
            type="text"
            formControlName="category"
            placeholder="e.g., Invoice"
            [matAutocomplete]="categoryAuto"
            (input)="categoryInputValue.set($any($event.target).value)"
            class="flex h-9 w-full rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-zinc-100 px-3 py-1.5 text-sm shadow-sm transition-colors placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
            [class.border-red-300]="form.get('category')?.invalid && form.get('category')?.touched"
            [class.ring-red-500]="form.get('category')?.invalid && form.get('category')?.touched"
          >
          <mat-autocomplete #categoryAuto="matAutocomplete">
            @for (cat of filteredCategories(); track cat) {
              <mat-option [value]="cat">{{ cat }}</mat-option>
            }
          </mat-autocomplete>
          @if (form.get('category')?.disabled) {
            <p class="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1">
              <mat-icon class="!text-sm !w-3.5 !h-3.5 !leading-3.5">lock</mat-icon>
              Locked to "anchor" while this field is an anchor.
            </p>
          } @else {
            <p class="text-xs text-zinc-400 dark:text-zinc-500">Pick an existing category or type a new one to create it.</p>
          }
          @if (form.get('category')?.hasError('required') && form.get('category')?.touched) {
            <p class="text-xs text-red-500 mt-1">Category is required</p>
          }
        </div>

        <!-- Description -->
        <div class="space-y-1.5">
          <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Description</label>
          <textarea
            data-testid="field-dialog-description-input"
            formControlName="description"
            cdkTextareaAutosize
            cdkAutosizeMinRows="2"
            cdkAutosizeMaxRows="10"
            placeholder="Describe the purpose of this field..."
            class="w-full rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-zinc-100 px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[64px]"
          ></textarea>
        </div>
      </section>

      <!-- ============ Section: Type & Values ============ -->
      <section class="space-y-4">
        <h3 class="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 border-b border-zinc-100 dark:border-zinc-800 pb-1.5">Type &amp; Values</h3>

        <div class="grid gap-4" [class.grid-cols-2]="!isObjectType()" [class.grid-cols-1]="isObjectType()">
          <div class="space-y-1.5">
            <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Type <span class="text-red-500">*</span></label>
            <div class="relative">
              <select
                data-testid="field-dialog-type-select"
                formControlName="type"
                class="flex h-9 w-full appearance-none rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-zinc-100 pl-3 pr-8 py-1.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              >
                @for (type of fieldTypes; track type.value) {
                  <option [value]="type.value">{{ type.label }}</option>
                }
              </select>
              <mat-icon class="absolute right-2 top-1/2 -translate-y-1/2 !text-lg text-zinc-400 dark:text-zinc-500 pointer-events-none">expand_more</mat-icon>
            </div>
          </div>

          @if (!isObjectType()) {
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Default Value</label>
              <app-default-value-input
                formControlName="defaultValue"
                [fieldType]="form.get('type')?.value || 'string'"
              />
            </div>
          }
        </div>

        <!-- Field Picker for object types -->
        @if (isObjectType()) {
          <div class="space-y-1.5 flex flex-col gap-2" style="height: min(480px, 65dvh)">
            <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Compose Fields <span class="text-red-500">*</span></label>
            <p class="text-xs text-zinc-400 dark:text-zinc-500 -mt-1">Object fields are built from other fields — select the ones that belong to this object.</p>
            @defer {
              <app-field-picker
                [allFields]="pickerFields()"
                [selectedIds]="selectedFieldIds()"
                (selectedIdsChange)="onFieldIdsChange($event)"
              />
            } @placeholder {
              <div class="flex items-center justify-center py-8 text-zinc-400">
                <span class="text-sm">Loading fields...</span>
              </div>
            }
          </div>
        }

        <!-- Enum Values (Conditional) -->
        @if (showEnumValues()) {
          <div class="space-y-1.5">
            <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Allowed Values
              <span class="text-zinc-400 font-normal">(Optional)</span>
            </label>
            <p class="text-xs text-zinc-400 dark:text-zinc-500">Restrict extraction to a fixed set of values. Press Enter to add each value; Backspace removes the last one.</p>
            <div class="flex flex-wrap gap-2 rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all min-h-[38px]">
              @for (tag of enumValuesArray(); track tag) {
                <span class="inline-flex items-center gap-1 rounded-md bg-zinc-100 dark:bg-zinc-700 px-2 py-0.5 text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  {{ tag }}
                  <button
                    type="button"
                    [attr.aria-label]="'Remove ' + tag"
                    (click)="removeEnumValue(tag)"
                    class="rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-600 p-0.5 cursor-pointer"
                  >
                    <mat-icon class="!text-xs !w-3 !h-3 !leading-3 text-zinc-500 dark:text-zinc-400">close</mat-icon>
                  </button>
                </span>
              }
              <input
                data-testid="field-dialog-enum-input"
                type="text"
                [value]="enumInputValue()"
                (input)="onEnumInputChange($event)"
                (keydown)="onEnumKeyDown($event)"
                [placeholder]="enumValuesArray().length === 0 ? 'Type value and press Enter...' : ''"
                class="flex-1 bg-transparent outline-none placeholder:text-zinc-400 dark:placeholder:text-zinc-500 dark:text-zinc-100 min-w-[120px] py-0.5"
              >
            </div>
          </div>
        }
      </section>

      <!-- ============ Section: Behavior ============ -->
      <section class="space-y-3">
        <h3 class="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 border-b border-zinc-100 dark:border-zinc-800 pb-1.5">Behavior</h3>

        <label class="flex items-start gap-3 cursor-pointer select-none rounded-lg border border-zinc-100 dark:border-zinc-800 p-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
          <button
            data-testid="field-dialog-required-toggle"
            type="button"
            role="switch"
            [attr.aria-checked]="form.get('required')?.value"
            (click)="toggleRequired()"
            class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 mt-0.5"
            [class.bg-blue-600]="form.get('required')?.value"
            [class.bg-zinc-200]="!form.get('required')?.value"
            [class.dark:bg-zinc-600]="!form.get('required')?.value"
          >
            <span
              class="pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform"
              [class.translate-x-4]="form.get('required')?.value"
              [class.translate-x-0]="!form.get('required')?.value"
            ></span>
          </button>
          <span class="flex flex-col">
            <span class="text-sm font-medium text-zinc-700 dark:text-zinc-300">Required</span>
            <span class="text-xs text-zinc-400 dark:text-zinc-500">Extraction fails validation when this field is missing from a document.</span>
          </span>
        </label>

        <label class="flex items-start gap-3 cursor-pointer select-none rounded-lg border border-zinc-100 dark:border-zinc-800 p-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
          <button
            data-testid="field-dialog-always-visible-toggle"
            type="button"
            role="switch"
            [attr.aria-checked]="form.get('isAlwaysVisible')?.value"
            (click)="toggleAlwaysVisible()"
            class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 mt-0.5"
            [class.bg-blue-600]="form.get('isAlwaysVisible')?.value"
            [class.bg-zinc-200]="!form.get('isAlwaysVisible')?.value"
            [class.dark:bg-zinc-600]="!form.get('isAlwaysVisible')?.value"
          >
            <span
              class="pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform"
              [class.translate-x-4]="form.get('isAlwaysVisible')?.value"
              [class.translate-x-0]="!form.get('isAlwaysVisible')?.value"
            ></span>
          </button>
          <span class="flex flex-col">
            <span class="text-sm font-medium text-zinc-700 dark:text-zinc-300">Always Visible</span>
            <span class="text-xs text-zinc-400 dark:text-zinc-500">Shown by default in results, even when the document has no value for it.</span>
          </span>
        </label>
      </section>

      <!-- ============ Section: Anchor ============ -->
      <section class="space-y-3">
        <h3 class="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 border-b border-zinc-100 dark:border-zinc-800 pb-1.5">Anchor</h3>

        <div class="rounded-lg border p-3 transition-colors"
             [class.border-amber-300]="form.get('anchorField')?.value"
             [class.dark:border-amber-700]="form.get('anchorField')?.value"
             [class.bg-amber-50/50]="form.get('anchorField')?.value"
             [class.dark:bg-amber-900/10]="form.get('anchorField')?.value"
             [class.border-zinc-100]="!form.get('anchorField')?.value"
             [class.dark:border-zinc-800]="!form.get('anchorField')?.value">
          <label class="flex items-start gap-3 cursor-pointer select-none">
            <button
              data-testid="field-dialog-anchor-field-toggle"
              type="button"
              role="switch"
              [attr.aria-checked]="form.get('anchorField')?.value"
              (click)="toggleAnchorField()"
              class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 mt-0.5"
              [class.bg-amber-500]="form.get('anchorField')?.value"
              [class.bg-zinc-200]="!form.get('anchorField')?.value"
              [class.dark:bg-zinc-600]="!form.get('anchorField')?.value"
            >
              <span
                class="pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform"
                [class.translate-x-4]="form.get('anchorField')?.value"
                [class.translate-x-0]="!form.get('anchorField')?.value"
              ></span>
            </button>
            <span class="flex flex-col">
              <span class="text-sm font-medium text-zinc-700 dark:text-zinc-300">Anchor Field</span>
              <span class="text-xs text-zinc-400 dark:text-zinc-500">Anchors are extracted from every document and used to match it to a template. Enabling this sets the category to "anchor".</span>
            </span>
          </label>

          @if (form.get('anchorField')?.value) {
            <div class="mt-3 pl-12 space-y-3">
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-300" for="min-tolerance-input">Min Tolerance</label>
                <input
                  id="min-tolerance-input"
                  data-testid="field-dialog-min-tolerance-input"
                  type="number"
                  formControlName="minTolerance"
                  min="0"
                  max="1"
                  step="0.01"
                  placeholder="0.00"
                  class="h-9 w-28 rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-zinc-100 px-2 py-0.5 text-sm shadow-sm transition-colors placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
                  [class.border-red-300]="form.get('minTolerance')?.invalid && form.get('minTolerance')?.touched"
                  [class.ring-red-500]="form.get('minTolerance')?.invalid && form.get('minTolerance')?.touched"
                >
                <p class="text-xs text-zinc-400 dark:text-zinc-500">Minimum match confidence between 0 and 1, up to 2 decimals.</p>
                @if (form.get('minTolerance')?.touched) {
                  @if (form.get('minTolerance')?.hasError('min') || form.get('minTolerance')?.hasError('max')) {
                    <p class="text-xs text-red-500">Min Tolerance: value must be between 0 and 1</p>
                  } @else if (form.get('minTolerance')?.hasError('maxTwoDecimals')) {
                    <p class="text-xs text-red-500">Min Tolerance: only up to 2 decimal digits are allowed</p>
                  }
                }
              </div>

              <div
                data-testid="field-dialog-anchor-field-warning"
                class="flex items-start gap-2 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
              >
                <mat-icon class="!text-base !w-4 !h-4 !leading-4 mt-0.5 shrink-0">warning</mat-icon>
                <span>
                  This field will be used for every single document and template to match a document to a template.
                  Changing it will mean that you have to manually re-extract each and every single template one by one.
                </span>
              </div>
            </div>
          }
        </div>
      </section>
    </div>

    <!-- Actions (sticky footer) -->
    <div class="flex justify-end gap-3 px-6 py-4 border-t border-zinc-200 dark:border-zinc-700 shrink-0 bg-white dark:bg-zinc-900">
      <button
        data-testid="field-dialog-cancel-btn"
        type="button"
        class="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-colors cursor-pointer"
        (click)="onCancel()"
      >
        Cancel
      </button>
      <button
        data-testid="field-dialog-submit-btn"
        type="submit"
        class="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        [disabled]="form.invalid || submitting()"
      >
        @if (submitting()) {
          <mat-spinner diameter="16" class="mr-2"></mat-spinner>
        }
        {{ submitButtonText() }}
      </button>
    </div>
  </form>
</div>
